import {
  Box,
  Button,
  Card,
  CardBody,
  Center,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Select,
  Stack,
  Switch,
  Text,
  useToast,
} from '@chakra-ui/react'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { periodEnum } from '../../elements/building/buildingConfiguration'
import Loader from '../../elements/loader'
import {
  Reporting,
  useReportingUpdateMutation,
  UserGetOneQuery,
} from '../../graphql/generated/graphql'

const ReportingTab = ({
  userDetails,
}: {
  userDetails?: UserGetOneQuery['userGetOne']
}) => {
  const { handleSubmit, register, control, reset } = useForm<Reporting>({
    defaultValues: userDetails?.reporting,
  })
  const toast = useToast()

  useEffect(() => {
    if (userDetails?.reporting) {
      reset(userDetails.reporting)
    }
  }, [userDetails, reset])

  const [updateReportSettings, { loading: onSaveLoading }] =
    useReportingUpdateMutation()

  const reportingDetails = userDetails?.reporting

  if (userDetails?.reporting) {
    const onFormSubmit = (data: Reporting) => {
      const removedName = data.buildings.map((building) => {
        return {
          buildingId: building.buildingId,
          buildingEnabled: building.buildingEnabled,
          buildingPeriod: building.buildingPeriod,
        }
      })
      const mutationData = {
        portfolioEnabled: data.portfolioEnabled,
        portfolioPeriod: data.portfolioPeriod,
        userId: userDetails.id!,
        buildings: removedName,
      }
      updateReportSettings({
        variables: { data: mutationData },
        onCompleted: () => {
          toast({
            title: 'Successfully updated the report settings',
            status: 'success',
            position: 'top',
            duration: 3000,
            isClosable: true,
          })
        },
        onError: (err) => {
          toast({
            title: err.networkError?.message,
            status: 'error',
            position: 'top',
            duration: 3000,
            isClosable: true,
          })
        },
      })
    }

    return (
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Box>
          <Heading>Portfolio Report</Heading>
          <Center justifyContent={'space-between'} mt={5}>
            <FormControl flexBasis={'50%'}>
              <FormLabel>Period</FormLabel>
              <Controller
                name="portfolioPeriod"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={
                      field.value
                        ? field.value
                        : reportingDetails?.portfolioPeriod
                    }
                    textTransform={'capitalize'}
                  >
                    <option value={periodEnum.weekly}>
                      {periodEnum.weekly}
                    </option>
                    <option value={periodEnum.monthly}>
                      {periodEnum.monthly}
                    </option>
                    <option value={periodEnum.yearly}>
                      {periodEnum.yearly}
                    </option>
                  </Select>
                )}
              />
            </FormControl>

            <FormControl flexBasis={'50%'} ml={5}>
              <FormLabel>Report</FormLabel>
              <Controller
                name="portfolioEnabled"
                control={control}
                render={({ field }) => (
                  <Switch
                    {...field}
                    value={`${field.value}`}
                    isChecked={
                      field.value !== undefined
                        ? field.value
                        : reportingDetails?.portfolioEnabled
                    }
                  />
                )}
              />
            </FormControl>
          </Center>
          <Heading mt={8}>Buildings Report</Heading>
          <Flex>
            {reportingDetails?.buildings?.map((building, i) => (
              <Card maxW="sm" mt={8} key={i} mr={8}>
                <CardBody>
                  <Stack spacing="3">
                    <Heading size="md">{building.buildingName}</Heading>
                    <Text>
                      You enable or disable the building's report from here
                    </Text>
                    <Center justifyContent={'space-between'} mt={5}>
                      <input
                        type="hidden"
                        value={building.buildingId}
                        {...register(`buildings.${i}.buildingId`)}
                      />
                      <FormControl flexBasis={'50%'}>
                        <FormLabel>Period</FormLabel>
                        <Controller
                          name={`buildings.${i}.buildingPeriod`}
                          control={control}
                          render={({ field }) => (
                            <Select
                              textTransform={'capitalize'}
                              {...field}
                              value={
                                field.value
                                  ? field.value
                                  : building.buildingPeriod
                              }
                            >
                              <option value={periodEnum.weekly}>
                                {periodEnum.weekly}
                              </option>
                              <option value={periodEnum.monthly}>
                                {periodEnum.monthly}
                              </option>
                              <option value={periodEnum.yearly}>
                                {periodEnum.yearly}
                              </option>
                            </Select>
                          )}
                        />
                      </FormControl>
                      <FormControl flexBasis={'50%'} ml={5}>
                        <FormLabel>Report</FormLabel>
                        <Controller
                          name={`buildings.${i}.buildingEnabled`}
                          control={control}
                          render={({ field }) => (
                            <Switch
                              {...field}
                              value={`${field.value}`}
                              isChecked={
                                field.value !== undefined
                                  ? field.value
                                  : building.buildingEnabled
                              }
                            />
                          )}
                        />
                      </FormControl>
                    </Center>
                  </Stack>
                </CardBody>
              </Card>
            ))}
          </Flex>
          <Button
            colorScheme={'purple'}
            mt={4}
            type={'submit'}
            isLoading={onSaveLoading}
          >
            Save
          </Button>
        </Box>
      </form>
    )
  } else {
    return <Loader />
  }
}

export default ReportingTab
